import { useEffect, useRef } from "react";
import { ParticipationEventState } from "@Interfaces";
import { ParticipationUsageService } from "@Services/API/Participation";
import useVisibleDuration from "./useVisibleDuration";

/**
 * Usage telemetry for the Participation tab: one "tab opened" per event per
 * visit, carrying the event's current state, plus time with numbers on screen.
 *
 * `hasNumbers` is true while the tab shows this event's numbers (no spinner,
 * error or unsupported parent event). `isLoading` is true while a fetch runs.
 */
const useParticipationUsageTracking = (
    channelId: string | undefined,
    state: ParticipationEventState,
    isLoading: boolean,
    hasNumbers: boolean
) => {
    // The store keeps the last numbers per event, so a cached state can be
    // shown on return. Report only after a fetch made during this visit.
    const fetchedChannelRef = useRef<string | null>(null);
    const reportedChannelRef = useRef<string | null>(null);

    useEffect(() => {
        if (!channelId) return;
        if (isLoading) {
            fetchedChannelRef.current = channelId;
            return;
        }
        if (
            !hasNumbers ||
            fetchedChannelRef.current !== channelId ||
            reportedChannelRef.current === channelId
        ) {
            return;
        }
        reportedChannelRef.current = channelId;
        ParticipationUsageService.reportTabOpened(channelId, state);
    }, [channelId, state, isLoading, hasNumbers]);

    useVisibleDuration(
        hasNumbers ? channelId : undefined,
        ParticipationUsageService.reportVisibleDuration
    );
};

export default useParticipationUsageTracking;
